import { inject, Injectable } from '@angular/core';
import { memo } from 'helpful-decorators';

import { PatientCurveQuery } from '@models/patient-curve/patient-curve.query';
import { PatientCurveState } from '@models/patient-curve/patient-curve.store';
import { SiteCurveQuery } from '@models/site-curve/site-curve.query';

import { EditableListDropdownItem } from '@shared/components/editable-list-dropdown/editable-list-dropdown-item.model';
import {
  DriverPatientGroup,
  DriverSiteGroup,
  ForecastMethodType,
} from '@shared/services/gql.service';
import { Option } from '@shared/types/components.type';

import { FullActivity, FullCategory } from '../../../state/category/category.query';
import {
  ForecastTableGridSiteCurveInterface,
  ForecastTableGridSiteCurveResultsInterface,
} from '../models/forecast-table-grid.model';

@Injectable({
  providedIn: 'root',
})
export class ForecastTableGridMethodService {
  private patientCurveQuery = inject(PatientCurveQuery);
  private siteCurveQuery = inject(SiteCurveQuery);

  // Method options when Driver is Time
  MethodOptions: Option<ForecastMethodType>[] = [
    {
      label: 'Straight Line',
      value: ForecastMethodType.FORECAST_STRAIGHTLINE,
    },
    {
      label: 'Frontloaded',
      value: ForecastMethodType.FORECAST_FRONTLOADED,
    },
    {
      label: 'Backloaded',
      value: ForecastMethodType.FORECAST_BACKLOADED,
    },
  ];

  getPatientGroupName(id: null | string | undefined): DriverPatientGroup | null {
    if (typeof id !== 'string') {
      return null;
    }

    return this.patientCurveQuery.getEntity(id) || null;
  }

  getSiteGroupName(id: null | string | undefined): DriverSiteGroup | null {
    if (typeof id !== 'string') {
      return null;
    }

    return this.siteCurveQuery.getEntity(id) || null;
  }

  @memo()
  getTimeMethod(forecast_method: null | string): string {
    const val = this.MethodOptions.filter((method) => method.value === forecast_method);

    return val[0]?.label || '';
  }

  parseCategoryMethod(category: FullActivity | FullCategory): string {
    return category.primary_settings.forecast_method || '';
  }

  parsePatientCurves(patientCurves: PatientCurveState[]): EditableListDropdownItem[] {
    if (!patientCurves || !patientCurves.length) {
      return [];
    }

    let flag = false;

    return patientCurves.map((patientCurve) => {
      if (!patientCurve.is_blended && !flag) {
        flag = true;

        return {
          isDeletable: false,
          isEditable: false,
          name: patientCurve.name,
          showLine: true,
          value: patientCurve.driver_setting_id,
        };
      }

      return {
        isDeletable: false,
        isEditable: false,
        name: patientCurve.name,
        showLine: false,
        value: patientCurve.driver_setting_id,
      };
    });
  }

  parseSiteCurves(
    siteCurves: ForecastTableGridSiteCurveResultsInterface
  ): ForecastTableGridSiteCurveInterface[] {
    if (!siteCurves.success || siteCurves.errors.length || !siteCurves.data) {
      return [];
    }

    let flagCurveType = '';

    return siteCurves.data
      .sort((a, b) => (a.curve_type < b.curve_type ? -1 : 1))
      .map((siteCurve) => {
        if (!siteCurve.is_blended && flagCurveType !== siteCurve.curve_type) {
          flagCurveType = siteCurve.curve_type;

          return {
            curveType: siteCurve.curve_type,
            label: siteCurve.name,
            showLine: true,
            value: siteCurve.driver_setting_id,
          };
        }

        return {
          curveType: siteCurve.curve_type,
          label: siteCurve.name,
          showLine: false,
          value: siteCurve.driver_setting_id,
        };
      });
  }
}
